import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, CheckCircle, Target, Clock, Shield, Sparkle, PaperPlaneRight } from "@phosphor-icons/react";
import StatsCounter from "@/components/StatsCounter";
import ServiceCard from "@/components/ServiceCard";
import TestimonialMarquee from "@/components/TestimonialMarquee";
import NorthAmericaMap from "@/components/NorthAmericaMap";
import QuoteCalculator from "@/components/QuoteCalculator";
import CTASection from "@/components/CTASection";
import PortfolioSlider from "@/components/PortfolioSlider";
import {
    ContourLines, SurveyingScene, CompassRose,
    FloatingAnnotations, SurveyPoint, SubdivisionGrid,
} from "@/components/decorative/SurveyingElements";
import { fetchServices, fetchTestimonials } from "@/lib/api";
import { IMG } from "@/lib/images";

export default function Home() {
    const [services, setServices] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const heroRef = useRef(null);
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);
    const sx = useSpring(mouseX, { stiffness: 60, damping: 25 });
    const sy = useSpring(mouseY, { stiffness: 60, damping: 25 });
    const parallaxX = useTransform(sx, [-1, 1], [-20, 20]);
    const parallaxY = useTransform(sy, [-1, 1], [-15, 15]);
    const parallaxX2 = useTransform(sx, [-1, 1], [40, -40]);
    const parallaxY2 = useTransform(sy, [-1, 1], [30, -30]);

    const onMouseMove = (e) => {
        if (!heroRef.current) return;
        const r = heroRef.current.getBoundingClientRect();
        mouseX.set(((e.clientX - r.left) / r.width) * 2 - 1);
        mouseY.set(((e.clientY - r.top) / r.height) * 2 - 1);
    };

    useEffect(() => {
        fetchServices().then(setServices).catch(() => {});
        fetchTestimonials().then(setTestimonials).catch(() => {});
    }, []); // fetchServices/fetchTestimonials are stable module-level imports; setState setters are stable — safe to omit from deps.

    useEffect(() => {
        document.title = "Surveys2CAD | Home";
    }, []);

    return (
        <div>
            {/* HERO */}
            <section
                ref={heroRef}
                onMouseMove={onMouseMove}
                className="relative overflow-hidden"
                data-testid="hero-section"
            >
                {/* Background layers — birdseye aerial of planned subdivision */}
                <div className="absolute inset-0 survey-grid opacity-25" />
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: `url(${IMG.heroBackground})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        opacity: 0.55,
                        filter: "saturate(0.85) contrast(1.05) brightness(0.95)",
                    }}
                    data-testid="hero-bg-image"
                />
                {/* Color + darkening overlay so text stays readable */}
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(3,7,18,0.55) 0%, rgba(3,7,18,0.35) 45%, rgba(3,7,18,0.85) 100%)",
                    }}
                />
                {/* Aurora gradients */}
                <div className="absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(900px 500px at 80% 30%, rgba(6,182,212,0.22), transparent 65%)," +
                            "radial-gradient(700px 500px at 10% 70%, rgba(168,85,247,0.18), transparent 65%)," +
                            "radial-gradient(650px 400px at 55% 90%, rgba(251,146,60,0.12), transparent 70%)",
                        mixBlendMode: "screen",
                    }}
                />

                {/* Decorative surveying elements with parallax */}
                <motion.div
                    style={{ x: parallaxX2, y: parallaxY2 }}
                    className="absolute inset-0 pointer-events-none"
                >
                    <SurveyingScene className="absolute inset-0 w-full h-full" opacity={0.38} />
                </motion.div>

                <motion.div
                    style={{ x: parallaxX, y: parallaxY }}
                    className="absolute -top-10 -right-20 w-[640px] h-[640px] pointer-events-none"
                >
                    <ContourLines className="w-full h-full" opacity={0.3} count={9} />
                </motion.div>

                <motion.div
                    style={{ x: parallaxX, y: parallaxY }}
                    className="absolute -bottom-16 -left-16 w-[420px] h-[260px] pointer-events-none opacity-30"
                >
                    <SubdivisionGrid className="w-full h-full" opacity={0.45} />
                </motion.div>

                {/* Floating field annotations */}
                <FloatingAnnotations />

                {/* Compass rose top-right */}
                <motion.div
                    style={{ x: parallaxX, y: parallaxY }}
                    className="absolute top-24 right-10 hidden lg:block pointer-events-none"
                >
                    <CompassRose size={110} />
                </motion.div>

                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#030712]/50 to-[#030712]" />

                <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-24 md:pt-32 md:pb-32">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-3xl"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15 }}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/5 mb-6 backdrop-blur-sm"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            <span className="overline !text-emerald-300">
                                Accepting projects · USA & Canada · 50+ Regions
                            </span>
                        </motion.div>

                        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tighter">
                            <motion.span
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2, duration: 0.7 }}
                                className="block text-slate-50"
                            >
                                Precision
                            </motion.span>
                            <motion.span
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.35, duration: 0.7 }}
                                className="block"
                            >
                                <span
                                    className="bg-clip-text text-transparent neon-text"
                                    style={{ backgroundImage: "linear-gradient(120deg, #22d3ee 0%, #3b82f6 35%, #a855f7 70%, #f97316 100%)" }}
                                >
                                    Land Surveying
                                </span>
                            </motion.span>
                            <motion.span
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5, duration: 0.7 }}
                                className="block text-slate-50"
                            >
                                CAD Drafting, <span className="italic font-light text-slate-300">on demand.</span>
                            </motion.span>
                        </h1>

                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.8, duration: 0.6 }}
                            className="text-lg md:text-xl text-slate-300 mt-6 max-w-2xl leading-relaxed"
                        >
                            Boundary, topographic, ALTA/NSPS, as-built, subdivision plats, FEMA certificates, and
                            point-cloud-to-CAD — delivered in AutoCAD, Civil 3D, and Revit by a team of 42+ specialists.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1, duration: 0.5 }}
                            className="mt-9 flex flex-wrap items-center gap-3"
                            data-testid="hero-ctas"
                        >
                            <Link to="/get-started" className="btn-primary group" data-testid="hero-get-started-btn">
                                Start a Project
                                <PaperPlaneRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
                            </Link>
                            <Link to="/services" className="btn-ghost group" data-testid="hero-services-btn">
                                Explore Services
                                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                            </Link>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1.2, duration: 0.5 }}
                            className="mt-10 flex flex-wrap gap-5 text-xs text-slate-400 font-mono-alt"
                        >
                            <span className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-cyan-400" /> 24-hour turnaround available</span>
                            <span className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-orange-400" /> NDA-backed confidentiality</span>
                            <span className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-violet-400" /> Fixed-price quotes</span>
                        </motion.div>
                    </motion.div>

                    {/* Floating stat overlay card (enhanced with survey points) */}
                    <motion.div
                        initial={{ opacity: 0, x: 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6, duration: 0.6 }}
                        style={{ x: parallaxX2, y: parallaxY2 }}
                        className="hidden lg:block absolute right-8 top-80 float-slow"
                    >
                        <div className="tech-card p-5 w-[320px] relative overflow-hidden group hover:!border-cyan-500/60 transition-all">
                            <div className="absolute -top-4 -right-4 w-24 h-24 opacity-30 group-hover:opacity-60 transition-opacity">
                                <svg viewBox="0 0 80 80">
                                    <SurveyPoint x="40" y="40" type="BM" label="BM 1248.75" delay={0.8} />
                                </svg>
                            </div>
                            <div className="overline mb-3">Live ops · Q4 2025</div>
                            <div className="flex items-end gap-3">
                                <div className="text-4xl font-heading font-bold text-slate-50 font-mono-alt">330k+</div>
                                <div className="text-xs text-slate-400 pb-1.5">drawings shipped</div>
                            </div>
                            <div className="mt-4 h-16 flex items-end gap-1">
                                {[22, 35, 28, 50, 38, 62, 45, 70, 55, 82, 60, 90].map((h, i) => {
                                    const month = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][i];
                                    let gradient;
                                    if (h > 70) {
                                        gradient = "linear-gradient(180deg, #f97316, rgba(249,115,22,0.2))";
                                    } else if (h > 50) {
                                        gradient = "linear-gradient(180deg, #a855f7, rgba(168,85,247,0.2))";
                                    } else {
                                        gradient = `linear-gradient(180deg, rgba(6,182,212,${h / 100}), rgba(6,182,212,0.1))`;
                                    }
                                    return (
                                        <motion.div
                                            key={month}
                                            initial={{ height: 0 }}
                                            animate={{ height: `${h}%` }}
                                            transition={{ delay: 0.9 + i * 0.05, duration: 0.5 }}
                                            className="flex-1 rounded-sm"
                                            style={{ background: gradient }}
                                        />
                                    );
                                })}
                            </div>
                            <div className="mt-3 text-xs text-slate-500 font-mono-alt flex justify-between">
                                <span>Q1</span><span>Q2</span><span>Q3</span><span>Q4</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* STATS */}
            <section className="max-w-7xl mx-auto px-5 lg:px-8 -mt-14 relative z-10" data-testid="stats-section">
                <StatsCounter />
            </section>

            {/* SERVICES OVERVIEW */}
            <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="services-overview">
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
                    <div>
                        <div className="overline mb-3">What we draft</div>
                        <h2 className="font-heading text-4xl md:text-5xl font-semibold text-slate-50 tracking-tight">
                            12 specialized survey <br className="hidden md:block" />
                            <span className="text-cyan-400">CAD drafting</span> services.
                        </h2>
                    </div>
                    <p className="text-slate-400 max-w-md">
                        Every drawing produced in-house by licensed surveyors and senior CAD technicians — then peer-reviewed
                        before delivery.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                    {services.slice(0, 12).map((s, i) => (
                        <motion.div
                            key={s.slug}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ delay: (i % 4) * 0.08 }}
                        >
                            <ServiceCard service={s} />
                        </motion.div>
                    ))}
                </div>

                <div className="mt-10 text-center">
                    <Link to="/services" className="btn-ghost" data-testid="view-all-services-btn">
                        View all services <ArrowRight size={16} />
                    </Link>
                </div>
            </section>

            {/* PORTFOLIO SLIDER */}
            <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20" data-testid="home-portfolio-slider">
                <div className="mb-10">
                    <div className="overline mb-3 text-cyan-300">Recent work · Preview only</div>
                    <h2 className="font-heading text-4xl md:text-5xl font-semibold text-slate-50 tracking-tight">
                        See the line-work <br className="hidden md:block" />
                        <span className="text-cyan-400">before you commit.</span>
                    </h2>
                </div>
                <PortfolioSlider variant="compact" />
            </section>

            {/* PROCESS */}
            <section className="bg-section-violet section-divider relative" data-testid="process-section">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
                    <div className="mb-12">
                        <div className="overline mb-3">How we work</div>
                        <h2 className="font-heading text-4xl md:text-5xl font-semibold text-slate-50 tracking-tight">
                            From field data to stamped CAD — <br className="hidden md:block" />
                            <span className="text-cyan-400">in four steps.</span>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative">
                        {[
                            { icon: Target, title: "1. Intake", desc: "Share field data (RAW, TXT, LAS, CSV) and deliverable specs via our secure portal." },
                            { icon: Sparkle, title: "2. Drafting", desc: "Senior CAD technicians produce DWG/DXF in AutoCAD or Civil 3D per your office standards." },
                            { icon: Shield, title: "3. QC Review", desc: "Every drawing peer-reviewed against a 40-point QA checklist before release." },
                            { icon: Clock, title: "4. Delivery", desc: "Stamped PDFs + native CAD files delivered in 24–72 hrs. Revisions included." },
                        ].map((step, i) => (
                            <motion.div
                                key={step.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                className="tech-card p-6 relative"
                                data-testid={`process-step-${i + 1}`}
                            >
                                <step.icon size={28} weight="duotone" className="text-cyan-300 mb-4" />
                                <h3 className="font-heading text-lg font-semibold text-slate-50 mb-2">{step.title}</h3>
                                <p className="text-sm text-slate-400 leading-relaxed">{step.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* QUOTE CALCULATOR + MAP */}
            <section className="bg-section-amber section-divider relative" data-testid="calc-map-section">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
                    <div className="lg:col-span-2">
                        <QuoteCalculator />
                    </div>
                    <div className="lg:col-span-3">
                        <div className="overline mb-3">Coverage & volume</div>
                        <h2 className="font-heading text-4xl md:text-5xl font-semibold text-slate-50 tracking-tight">
                            Drawings delivered across <span className="text-cyan-400">USA & Canada.</span>
                        </h2>
                        <p className="text-slate-400 mt-4 max-w-xl">
                            Highest volumes in Florida, Illinois, Texas, Georgia, Maryland, and Ohio — with strong
                            coverage across all 50 US states and Canadian provinces. Hover any region for live counts.
                        </p>
                        <div className="mt-6">
                            <NorthAmericaMap />
                        </div>
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="bg-section-emerald section-divider relative py-24" data-testid="testimonials-section">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 mb-10">
                    <div className="overline mb-3">Trusted by 6,000+ firms</div>
                    <h2 className="font-heading text-4xl md:text-5xl font-semibold text-slate-50 tracking-tight">
                        What professional surveyors <br className="hidden md:block" />
                        <span className="text-orange-400">say about our work.</span>
                    </h2>
                </div>
                <TestimonialMarquee testimonials={testimonials} />
            </section>

            {/* CTA */}
            <section className="bg-section-rose section-divider relative">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
                    <CTASection />
                </div>
            </section>
        </div>
    );
}
